import { useEffect, useState, useRef } from 'react'
import { Text, View, Platform } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { impact, notification, ImpactStyle, NotificationType } from '@/lib/utils/haptics'
import Animated, { FadeInDown, FadeInUp, useReducedMotion } from 'react-native-reanimated'
import { Pressable } from '../../components/ui/Pressable'
import { AuthOtpStep } from '../../components/auth/AuthOtpStep'
import { PhoneOtpDeliveryRecovery } from '../../components/auth/PhoneOtpDeliveryRecovery'
import { BackButton } from '../../components/ui/BackButton'
import { AuthScaffold } from '../../components/auth/AuthScaffold'
import { useAuth } from '../../lib/hooks/use-auth'
import { useAuthStore } from '../../lib/stores/auth.store'
import { authService } from '../../lib/services/auth.service'
import { productTelemetryService } from '../../lib/services/product-telemetry.service'
import { useBiometric } from '../../lib/hooks/use-biometric'
import { useAuthRhythm } from '../../lib/hooks/use-auth-rhythm'
import {
  ServiceError,
  classifyPhoneOtpDeliveryError,
  phoneOtpRetryAfterSeconds,
  type PhoneOtpDeliveryReason,
} from '../../lib/errors/service-error'
import { getContactVerificationRequirement } from '../../lib/utils/account-contact'
import { FONTS, SPACING } from '../../lib/brand'
import { openPhoneOtpSupport } from '../../lib/auth/open-phone-otp-support'
import { useSemanticColors, DURATION, INTERACTION, STAGGER } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { toast } from '../../lib/toast'
import { captureError, errorMessage } from '../../lib/utils/observability'

const isWeb = Platform.OS === 'web'
type PhoneAuthMode = 'login' | 'signup'

export default function PhoneVerifyScreen() {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop, width } = useBreakpoint()
  // Only non-PII flow flags come from the URL. The phone number is handed
  // over through the in-memory auth store because expo-router serialises
  // params into the web query string (browser history, hosting access logs,
  // Sentry browser-tracing URL capture).
  const { mode: rawMode } = useLocalSearchParams<{ mode?: string }>()
  const mode: PhoneAuthMode = rawMode === 'signup' ? 'signup' : 'login'
  const isSignup = mode === 'signup'
  const { sendOtp, sendOtpPending, verifyOtp, verifyOtpPending, signInWithGoogle, googlePending } =
    useAuth()
  const { isAvailable, hasBeenPrompted, markPrompted } = useBiometric()
  const setCompletingSignup = useAuthStore((s) => s.setCompletingSignup)
  const setPendingAuthPhone = useAuthStore((s) => s.setPendingAuthPhone)
  const markCredentialUnlock = useAuthStore((s) => s.markCredentialUnlock)
  // Snapshot once on mount: the store value is cleared right after so a later
  // visit to this screen can never inherit a stale number.
  const [phone] = useState(() => useAuthStore.getState().pendingAuthPhone)
  const reducedMotion = useReducedMotion()

  // One breakpoint for the whole auth flow — same source of truth login.tsx
  // and phone-login.tsx read, so the headline keeps its size across the flow.
  const isWide = isDesktop
  // Same cut-over AuthScaffold uses to pick between the centered desktop
  // column and the full-height mobile scroll body. Only mobile gets the
  // flexible footer spacer below.
  const isSplit = width >= 900

  const [error, setError] = useState('')
  const [deliveryIssue, setDeliveryIssue] = useState<{
    reason: PhoneOtpDeliveryReason
  } | null>(null)
  const [retrySeconds, setRetrySeconds] = useState(0)
  const [externalResendVersion, setExternalResendVersion] = useState(0)
  // Sync guards — complement TanStack's `verifyOtpPending` / `sendOtp` pending
  // state, which lag the tap by a render tick.
  const isVerifyingRef = useRef(false)
  const isResendingRef = useRef(false)
  const recordPhoneOtpEvent = (
    eventName: string,
    elementId: string,
    elementLabel: string,
    extra?: Record<string, unknown>,
  ) => {
    productTelemetryService.recordEvent('form_submit', eventName, {
      routePath: '/phone-verify',
      screenName: 'phone-verify',
      elementId,
      elementType: 'auth_step',
      metadata: {
        ...extra,
        elementLabel,
        mode,
        channel: 'whatsapp',
        phonePresent: Boolean(phone),
      },
    })
  }

  const sp = useAuthRhythm()

  // One-shot handoff: clear the staged number as soon as this screen has
  // snapshotted it, so the PII lives no longer than the OTP step needs it.
  useEffect(() => {
    setPendingAuthPhone(null)
  }, [setPendingAuthPhone])

  // Never leave isCompletingSignup true if the user backs out mid-biometric
  // after a successful OTP — that froze tabs on GateFallback forever.
  useEffect(() => {
    return () => {
      useAuthStore.getState().setCompletingSignup(false)
    }
  }, [])

  // The handoff is in-memory only, so a web refresh or a deep link straight to
  // /phone-verify legitimately has no phone. Send the user back to the phone
  // entry step instead of rendering an OTP screen that can never verify.
  useEffect(() => {
    if (phone) return
    router.replace({
      pathname: (isSignup ? '/(auth)/phone-signup' : '/(auth)/phone-login') as never,
      params: { mode },
    })
  }, [phone, isSignup, mode, router])

  const resolvePostAuthRoute = async (
    knownUser?: {
      email?: string | null
      email_confirmed_at?: string | null
      phone?: string | null
      phone_confirmed_at?: string | null
    } | null,
  ) => {
    // Same door as email/password login: one verified contact is enough to
    // enter. Phone signup used to hardcode verify-email here, so a person who
    // had just created the account was sent to "Agregá tu correo" — or, when
    // that screen was not in the auth Stack, bounced to welcome — and thought
    // registration had failed. The missing mailbox stays a pending step in
    // verification, not a gate.
    // Prefer the user object from the verify response when present — a
    // follow-up getSession() can lag SecureStore and look unauthenticated for
    // a beat right after OTP success.
    const sessionUser = knownUser ?? (await authService.getSession())?.user ?? null
    const requirement = getContactVerificationRequirement(sessionUser, null)
    if (requirement === 'email') return '/(auth)/verify-email'
    if (requirement === 'phone') return '/(auth)/verify-phone'
    return '/(tabs)/explore'
  }

  const enterAppAfterOtp = async (
    knownUser?: {
      email?: string | null
      email_confirmed_at?: string | null
      phone?: string | null
      phone_confirmed_at?: string | null
    } | null,
  ) => {
    // Hold isCompletingSignup through the replace so (tabs)/_layout cannot
    // Redirect to welcome on a one-frame isAuthenticated=false. Clear after
    // navigation is scheduled.
    const route = await resolvePostAuthRoute(knownUser).catch(() => '/(tabs)/explore')
    router.replace(route as never)
  }

  const handleComplete = async (code: string) => {
    if (!phone || isVerifyingRef.current || isResendingRef.current) {
      throw new Error('OTP request unavailable')
    }
    isVerifyingRef.current = true
    setError('')
    impact(ImpactStyle.Light)

    // Block AuthGate redirect BEFORE verifyOtp fires SIGNED_IN
    setCompletingSignup(true)

    // El usuario que devuelve la sesión recién instalada. `id` y `created_at`
    // hacen falta para completar el registro y para saber si la cuenta es nueva.
    let verifiedUser: {
      id?: string
      created_at?: string
      email?: string | null
      email_confirmed_at?: string | null
      phone?: string | null
      phone_confirmed_at?: string | null
    } | null = null

    try {
      recordPhoneOtpEvent(
        'auth.phone_otp_verify_started',
        'phone-verify.otp-input',
        'Verificar código',
      )
      const verifyResult = await verifyOtp({
        phone,
        code,
        // Solo el registro crea cuentas. Entrar con un número desconocido tiene
        // que decir "no hay cuenta con ese número"; fabricar una sería crear a
        // alguien que nadie pidió, sin consentimiento y sin perfil.
        allowCreate: isSignup,
        metadata: isSignup
          ? {
              preferredLocale: i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'es',
              // La aceptación legal se exigió en phone-signup antes de enviar;
              // llegar acá en modo registro es haberla marcado.
              acceptedTerms: true,
            }
          : undefined,
      })

      // El número quedó probado pero no abrió una puerta. Son dos casos y cada
      // uno tiene su propia salida, así que ninguno puede caer en "código
      // incorrecto" — el código era correcto.
      if (verifyResult.account === 'use_email_login') {
        throw new ServiceError({
          message: 'Phone account requires email login',
          code: 'OTP_USE_EMAIL_LOGIN',
          userMessageKey: 'auth.errors.phone_account_uses_email',
        })
      }
      if (verifyResult.account === 'not_found') {
        throw new ServiceError({
          message: 'Phone account not found',
          code: 'PHONE_ACCOUNT_NOT_FOUND',
          userMessageKey: 'auth.errors.phone_account_not_found',
        })
      }

      // La sesión de la respuesta primero, por la misma razón que en use-auth:
      // un getSession() inmediato puede ir detrás de SecureStore en nativo.
      const session = verifyResult.session ?? (await authService.getSession())
      if (
        verifyResult.account !== 'session' ||
        !session?.user.phone_confirmed_at ||
        session.user.phone?.replace(/\D/g, '') !== phone.replace(/\D/g, '')
      ) {
        throw new ServiceError({
          message: 'Phone OTP did not establish the requested account session',
          code: 'OTP_SESSION_UNRESOLVED',
          userMessageKey: 'auth.errors.service_unavailable',
        })
      }
      verifiedUser = session?.user ?? null
      recordPhoneOtpEvent(
        'auth.phone_otp_verified',
        'phone-verify.otp-input',
        'Teléfono verificado',
        { account: verifyResult.account },
      )

      const userId = verifiedUser?.id ?? null
      if (isSignup && userId) {
        const createdAt = verifiedUser?.created_at
        const isBrandNew = !createdAt || Date.now() - new Date(createdAt).getTime() < 5 * 60 * 1000
        if (!isBrandNew) {
          // Pantalla de registro usada con un número que ya existía → se entró
          // a la cuenta de siempre. Decirlo, en vez de dejar creer que se creó
          // una nueva.
          toast.info(t('auth.existing_phone_signed_in'))
        } else {
          try {
            await authService.completeSignup(userId, false)
          } catch (err) {
            // La cuenta y la sesión son válidas; no se deja a nadie varado en la
            // pantalla del código por una carrera del RPC de consentimiento.
            captureError(
              err,
              { screen: 'phone-verify', action: 'complete-phone-signup' },
              { userId },
            )
          }
        }
      }
    } catch (err) {
      recordPhoneOtpEvent(
        'auth.phone_otp_verify_failed',
        'phone-verify.otp-input',
        'Código incorrecto',
      )
      // Network / transport failures are not "código incorrecto" — that label
      // made people re-request SMS after a flaky radio and hit rate limits.
      const haystack = errorMessage(err, '')
      const looksNetwork =
        /failed to fetch|network request failed|load failed|timeout|offline|internet|conexión|conexion/i.test(
          haystack || String(err),
        )
      setError(
        errorMessage(err, looksNetwork ? t('auth.errors.network') : t('auth.errors.otp_invalid')),
      )
      void notification(NotificationType.Error)
      captureError(err, { screen: 'phone-verify', action: 'verify' })
      setCompletingSignup(false)
      isVerifyingRef.current = false
      throw err
    }

    void notification(NotificationType.Success)
    markCredentialUnlock()
    // Never interrupt OTP success with Face ID enrollment.
    try {
      const available = await isAvailable()
      const prompted = await hasBeenPrompted()
      if (available && !prompted) await markPrompted()
    } catch (err) {
      captureError(err, { screen: 'phone-verify', action: 'biometric-check' })
    } finally {
      isVerifyingRef.current = false
    }

    await enterAppAfterOtp(verifiedUser)
  }

  const handleResend = async () => {
    if (!phone || isResendingRef.current || isVerifyingRef.current) {
      throw new Error('OTP request unavailable')
    }
    isResendingRef.current = true
    setError('')
    impact(ImpactStyle.Light)
    try {
      recordPhoneOtpEvent(
        'auth.phone_otp_resend_started',
        'phone-verify.resend-button',
        'Reenviar código',
      )
      await sendOtp({ phone })
      setDeliveryIssue(null)
      setRetrySeconds(0)
      recordPhoneOtpEvent(
        'auth.phone_otp_resend_succeeded',
        'phone-verify.resend-button',
        'Código reenviado',
      )
    } catch (err) {
      recordPhoneOtpEvent(
        'auth.phone_otp_resend_failed',
        'phone-verify.resend-button',
        'No se pudo reenviar código',
      )
      if (__DEV__) console.error('[PhoneVerify] resend OTP failed:', err)
      const reason = classifyPhoneOtpDeliveryError(err)
      if (reason) {
        setDeliveryIssue({ reason })
        // El bloqueo lo decide el servidor, no esta pantalla. Cuando el rechazo
        // es "ya te mandé uno hace un momento" son 120 segundos, y contar menos
        // deja el botón vivo para gastar otro crédito en el mismo rechazo.
        setRetrySeconds(
          phoneOtpRetryAfterSeconds(err) ?? (reason === 'provider_rejected' ? 30 : 60),
        )
        setError('')
        // La tarjeta queda debajo de las casillas — el toast lleva el titular a
        // donde la persona está mirando, así una pantalla corta o un texto
        // grande no lo esconden.
        toast.error(
          reason === 'service_rate_limited'
            ? t('auth.verify_phone_sms_busy_title')
            : reason === 'account_rate_limited'
              ? t('auth.verify_phone_sms_quota_title')
              : t('auth.verify_phone_sms_provider_title'),
        )
      } else {
        setDeliveryIssue(null)
        toast.error(errorMessage(err, t('auth.errors.otp_resend_failed')))
      }
      void notification(NotificationType.Error)
      captureError(err, { screen: 'phone-verify', action: 'resend' })
      // Se relanza para que OtpInput NO borre los dígitos ni reinicie su cuenta
      // de 60s: un reenvío que falló no debe costarle a alguien el código que ya
      // tenía tecleado.
      throw err
    } finally {
      isResendingRef.current = false
    }
  }

  useEffect(() => {
    if (retrySeconds <= 0) return
    const id = setTimeout(() => setRetrySeconds((s) => Math.max(0, s - 1)), 1000)
    return () => clearTimeout(id)
  }, [retrySeconds])

  // Redirect above is in flight — render nothing rather than an OTP form with
  // no phone behind it.
  if (!phone) return null

  const webCursor = isWeb ? { cursor: 'pointer' as const } : {}

  const formContent = (
    <>
      {/* ─── Navigation ─── */}
      <BackButton />

      {/* ─── Header ─── */}
      <Animated.Text
        entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER)}
        accessibilityRole="header"
        numberOfLines={2}
        adjustsFontSizeToFit
        minimumFontScale={0.88}
        style={{
          fontFamily: FONTS.bodyBold,

          fontSize: isWide ? 28 : 30,
          lineHeight: isWide ? 34 : 36,
          marginTop: sp.headerTop,
          color: colors.textPrimary,
        }}
      >
        {t('auth.verify_phone_title')}
      </Animated.Text>

      <Animated.Text
        entering={
          reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 2)
        }
        style={{
          fontFamily: FONTS.body,

          fontSize: 15,
          lineHeight: 22,
          marginTop: sp.headlineToSub,
          marginBottom: sp.subToForm,
          color: colors.textSecondary,
        }}
      >
        {t('auth.verify_phone_desc', { phone })}
      </Animated.Text>

      {/* ─── OTP Input ─── */}
      <AuthOtpStep
        key={externalResendVersion}
        onComplete={handleComplete}
        onResend={handleResend}
        disabled={verifyOtpPending || sendOtpPending}
        error={error}
        testID="phone-verify.otp-input"
      />

      {deliveryIssue ? (
        <PhoneOtpDeliveryRecovery
          reason={deliveryIssue.reason}
          mode="resend"
          emailCta={isSignup ? 'create_account_email' : 'sign_in_email'}
          onEmailCta={() => {
            productTelemetryService.recordEvent('form_submit', 'auth.phone_otp_recovery_email', {
              routePath: '/phone-verify',
              screenName: 'phone-verify',
              elementId: 'phone-verify.recovery-email',
              elementType: 'button',
              metadata: { mode },
            })
            setPendingAuthPhone(null)
            router.replace(isSignup ? '/(auth)/register' : '/(auth)/login')
          }}
          onGoogleCta={() => {
            productTelemetryService.recordEvent('form_submit', 'auth.phone_otp_recovery_google', {
              routePath: '/phone-verify',
              screenName: 'phone-verify',
              elementId: 'phone-verify.recovery-google',
              elementType: 'button',
              metadata: { mode },
            })
            setPendingAuthPhone(null)
            void signInWithGoogle().catch((err) => {
              captureError(err, { screen: 'phone-verify', action: 'recovery-google' })
              toast.error(errorMessage(err, t('auth.errors.google_failed')))
            })
          }}
          googleLoading={googlePending}
          onRetry={() => {
            if (retrySeconds > 0 || isVerifyingRef.current || isResendingRef.current) return
            void handleResend()
              .then(() => setExternalResendVersion((version) => version + 1))
              .catch(() => {})
          }}
          retryLoading={sendOtpPending}
          retryDisabled={retrySeconds > 0 || verifyOtpPending || sendOtpPending}
          retrySeconds={retrySeconds}
          onChangeNumber={() => {
            setDeliveryIssue(null)
            setPendingAuthPhone(null)
            router.replace({
              pathname: '/(auth)/phone-login' as never,
              params: { mode },
            })
          }}
          onSupport={() => {
            void openPhoneOtpSupport(t('auth.verify_phone_sms_support_message'), {
              unavailableMessage: t('auth.verify_phone_sms_support_unavailable'),
              screen: 'phone-verify',
            })
          }}
          testID="phone-verify.delivery-recovery"
        />
      ) : null}

      {/* Mobile only: absorbs the leftover height so the footer closes the
          screen instead of floating with the display empty below it.
          Collapses to zero when the content already fills the viewport. */}
      {isSplit ? null : <View style={{ flex: 1 }} />}

      {/* ─── Footer ─── */}
      <Animated.View
        collapsable={false}
        entering={reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 5)}
        style={{
          flexDirection: 'row',
          // Measured on a Pixel 10 Pro XL at font_scale 2.0: the node bounds
          // reported 24..424dp inside a 448dp screen — i.e. "it fits" — while
          // the ink ran off both edges, because Android renders a squeezed
          // Text past its own view bounds instead of clipping it. Wrapping is
          // what actually puts the link on its own line.
          flexWrap: 'wrap',
          rowGap: SPACING.xs,
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: sp.altToFooter,
          paddingBottom: isWide ? 0 : SPACING.sm,
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.body,
            fontSize: 14,
            flexShrink: 1,
            color: colors.textSecondary,
          }}
        >
          {`${isSignup ? t('auth.already_have_account') : t('auth.dont_have_account')} `}
        </Text>
        <Pressable
          onPress={() =>
            isSignup
              ? router.replace('/(auth)/login')
              : router.replace({
                  pathname: '/(auth)/phone-signup' as never,
                  params: { mode: 'signup' },
                })
          }
          hitSlop={INTERACTION.iconHitSlop}
          testID="phone-verify.footer-link"
          // The link was a 20pt-tall text node. hitSlop stays for native, but
          // react-native-web drops it, so the box carries the 44 itself.
          style={{
            ...webCursor,
            minHeight: INTERACTION.minTouchTarget,
            justifyContent: 'center' as const,
            paddingHorizontal: SPACING.xs,
          }}
          accessibilityRole="link"
          accessibilityLabel={isSignup ? t('auth.login') : t('auth.register')}
        >
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 14,
              // `borderFocused` is a border token, and using it here painted
              // this link purple in light while every other auth footer in the
              // flow paints textAccent coral. One accent, both themes.
              color: colors.textAccent,
            }}
          >
            {isSignup ? t('auth.login') : t('auth.register')}
          </Text>
        </Pressable>
      </Animated.View>
    </>
  )

  return <AuthScaffold background={colors.background}>{formContent}</AuthScaffold>
}
